import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "iuni_cookie_consent";

const pushConsentToDataLayer = (status) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "cookie_consent_update", cookie_status: status });
};

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      // Persisted consent: re-signal GTM on every page load / SPA navigation
      pushConsentToDataLayer(stored === "all" ? "accepted" : "rejected");
      return;
    }
    const timer = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const choose = (choice) => {
    localStorage.setItem(STORAGE_KEY, choice);
    pushConsentToDataLayer(choice === "all" ? "accepted" : "rejected");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 48 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 32 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-50 bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm"
          data-testid="cookie-banner"
          role="dialog"
          aria-label="Consenso cookie"
        >
          <div className="relative bg-white/90 backdrop-blur-md border border-gray-200/80 shadow-2xl rounded-2xl p-6">
            <button
              data-testid="cookie-close-button"
              onClick={() => choose("necessary")}
              aria-label="Chiudi e accetta solo i cookie necessari"
              className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <X size={14} />
            </button>

            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl bg-[rgba(224,122,95,0.1)] flex items-center justify-center shrink-0">
                <Cookie size={16} className="text-[#E07A5F]" />
              </span>
              <p className="text-sm font-semibold text-[#1A1A1A]">La tua privacy è importante</p>
            </div>

            <p className="text-xs text-gray-500 leading-relaxed mt-3">
              Utilizziamo i cookie per offrirti un'esperienza di navigazione fluida e analizzare il
              traffico del nostro programma Beta. Puoi accettare tutti i cookie o gestire le tue
              preferenze. Leggi la nostra{" "}
              <Link
                to="/privacy-policy"
                data-testid="cookie-privacy-link"
                className="underline underline-offset-2 text-gray-600 hover:text-[#C2492F] transition-colors"
              >
                Privacy Policy
              </Link>
              .
            </p>

            <div className="flex gap-2.5 mt-5">
              <button
                data-testid="cookie-accept-all-button"
                onClick={() => choose("all")}
                className="flex-1 px-4 py-2.5 text-xs font-semibold text-white rounded-xl bg-gradient-to-r from-[#E07A5F] to-[#E2856E] shadow-[0_4px_14px_rgba(224,122,95,0.39)] hover:shadow-[0_6px_20px_rgba(224,122,95,0.3)] hover:-translate-y-0.5 transition-[box-shadow,transform]"
              >
                Accetta tutti
              </button>
              <button
                data-testid="cookie-necessary-button"
                onClick={() => choose("necessary")}
                className="flex-1 px-4 py-2.5 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Solo necessari
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
